import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTag } from '@fortawesome/pro-regular-svg-icons';
import { faShareNodes } from '@fortawesome/pro-light-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import {
  BundleCard,
  BundleCardFreeTrial,
} from '../viewModel/subscriptionBundle.vm.mapper';
import { Skeleton, Tooltip } from '@mui/material';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { sprinkles } from '@design-system/sprinkles.css';
import clsx from 'clsx';
import { Tag } from '@design-system/components/tag/tag';
import { Row } from '@design-system/components/grid/grid';

export const BundleDetailsCard = ({
  name,
  tooltipText,
  onClick,
  onShare,
  ...p
}: BundleCard) => (
  <div
    className={clsx(
      'flex flex-grow flex-col gap-2 p-4 rounded-[10px] shadow-exercise w-full',
      onClick && 'cursor-pointer',
      sprinkles({
        bg: {
          hover: 'N-10',
          lightMode: 'W-100',
        },
      }),
    )}
    onClick={() => onClick && onClick()}
    data-testid={`subscription-card-${p.testIdSuffix}`}
  >
    <div className="flex items-center gap-4">
      <Typography variant="headline" size="medium">
        {name}
      </Typography>
      {!tooltipText ? null : (
        <Tooltip
          title={
            <div className="flex flex-col gap-4">
              {tooltipText.map((text) => (
                <Typography key={text}>{text}</Typography>
              ))}
            </div>
          }
          placement="right-start"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'black',
                color: 'white',
                fontSize: '12px',
                fontWeight: 500,
                fontFamily: 'inherit',
                padding: '4px 8px',
              },
            },
          }}
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </Tooltip>
      )}
      {onShare ? (
        <div
          className={clsx(
            'ml-auto cursor-pointer',
            sprinkles({ color: 'B-100' }),
          )}
          onClick={(e) => {
            e.stopPropagation();
            onShare();
          }}
          data-testid={`subscription-card-share-${p.testIdSuffix}`}
        >
          <FontAwesomeIcon icon={faShareNodes} size="lg" />
        </div>
      ) : null}
    </div>
    <EditableDetails {...p} />
  </div>
);

const EditableDetails = ({
  freeTrial,
  showFreeTrial,
  testIdSuffix,
  price,
  submitting,
  draftMode,
}: Pick<
  BundleCard,
  | 'freeTrial'
  | 'showFreeTrial'
  | 'price'
  | 'testIdSuffix'
  | 'submitting'
  | 'draftMode'
>) => {
  if (submitting) {
    return (
      <div
        className="flex flex-col gap-4"
        data-testid={`subscription-card-loader-${testIdSuffix}`}
      >
        <Skeleton variant="rectangular" width="100%" height={30} />
        <Skeleton variant="rectangular" width="100%" height={30} />
      </div>
    );
  }

  return (
    <>
      {price ? (
        <div className="flex gap-2 items-center">
          <FontAwesomeIcon icon={faTag} />
          <Typography
            variant="body"
            size="large"
            fontWeight="normal"
            data-testid={`subscription-card-price-${testIdSuffix}`}
          >
            {price}
          </Typography>
        </div>
      ) : (
        <div className="flex items-center">
          <Typography
            className={sprinkles({
              color: 'P-80',
              textDecoration: 'underline',
            })}
          >
            Set price
          </Typography>
        </div>
      )}
      <Row>
        <FreeTrial freeTrial={freeTrial} showFreeTrial={showFreeTrial} />
        {draftMode && (
          <Tag
            readonly={true}
            label="Draft"
            className={clsx(
              sprinkles({
                bg: 'D-20',
                color: 'D-100',
                marginLeft: 'auto',
              }),
            )}
          />
        )}
      </Row>
    </>
  );
};

const FreeTrial = ({
  freeTrial,
  showFreeTrial,
}: Pick<BundleCard, 'freeTrial' | 'showFreeTrial'>) => {
  if (!freeTrial || !showFreeTrial) return null;

  return (
    <div className="flex gap-2 items-center">
      <Typography variant="body" size="large" fontWeight="normal">
        7-day free trial for:
      </Typography>
      <Typography variant="body" size="large" fontWeight="normal">
        <FreeTrialLabels freeTrial={freeTrial} />
      </Typography>
    </div>
  );
};

const FreeTrialLabels = ({
  freeTrial,
}: {
  freeTrial?: BundleCardFreeTrial;
}) => {
  if (!freeTrial) return null;

  const labels = [
    {
      title: 'monthly',
      value: freeTrial.monthly.checked,
    },
    {
      title: 'annual',
      value: freeTrial.annual.checked,
    },
  ];

  return (
    <div className="flex gap-4 items-center">
      {labels.map(({ title, value }) => (
        <div className="flex gap-2 items-center" key={title}>
          <FontAwesomeIcon
            icon={value ? faCheck : faXmark}
            className={sprinkles({
              color: value ? 'B-100' : 'N-40',
            })}
          />
          <Typography
            variant="body"
            size="large"
            fontWeight="normal"
            className={sprinkles({
              color: value ? 'B-100' : 'N-40',
            })}
            data-testid={`subscription-free-trial-label-${title}`}
            aria-disabled={!value}
          >
            {title}
          </Typography>
        </div>
      ))}
    </div>
  );
};
